<script setup>
import Logo from '@/Shared/SVG/Logo';
import FacebookIcon from '@/Shared/SVG/FacebookIcon';
import TwitterIcon from '@/Shared/SVG/TwitterIcon';
import InstagramIcon from '@/Shared/SVG/InstagramIcon';
import DiscordIcon from '@/Shared/SVG/DiscordIcon';
import YoutubeIcon from '@/Shared/SVG/YoutubeIcon';

import NavigationItem from '@/Shared/Navigation/NavigationItem';
import MobileNavigationItem from '@/Shared/Navigation/MobileNavigationItem';
import Footer from '@/Shared/Footer/Footer';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline';
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    PopoverGroup,
} from '@headlessui/vue';
import { computed } from 'vue';
import LinkedinIcon from '@/Shared/SVG/LinkedinIcon.vue';
import TelegramIcon from '@/Shared/SVG/TelegramIcon.vue';
import LanguageSwitch from '@/Shared/LanguageSwitch.vue';

const navigation = [
    {
        name: 'Platform',
        slug: 'platform',
        href: '/platform/build',
        current: false,
        external: false,
        withIcon: true,
    },
    {
        name: 'Gaming',
        slug: 'gaming',
        href: '/gaming/game-hub',
        current: false,
        external: false,
        withIcon: true,
    },
    {
        name: 'Finance',
        slug: 'finance',
        href: '/finance',
        current: false,
        external: false,
        withIcon: false,
    },
    // { name: 'Wodo Mini', slug: '#TODO', href: '#TODO', current: false, external: false, withIcon: false },
    {
        name: 'Company',
        slug: 'company',
        href: '/about',
        current: false,
        external: false,
        withIcon: true,
    },
    {
        name: 'Blogs',
        slug: 'blogs',
        href: '/blogs',
        current: false,
        external: false,
        withIcon: true,
    },
    {
        name: 'Docs',
        slug: 'docs',
        href: 'https://docs.wodo.io/wodo-gaming/',
        current: false,
        external: true,
        withIcon: false,
    },
    {
        name: 'Contact',
        slug: 'contact',
        href: '/contact',
        current: false,
        external: false,
        withIcon: false,
    },
];

let props = defineProps({
    config: {
        default: {
            current: false,
        },
    },
    data: Object,
    user: Object,
    errors: Object,
});

let config = computed(() => {
    return props.config;
});
if (!config.current) {
    config.current = 'home';
}
if (!config.metaTitle) {
    config.metaTitle = 'Wodo Network';
}
if (!config.metaDescription) {
    config.metaDescription = 'Wodo Network';
}
if (!config.ogImage) {
    config.ogImage = 'https://wodo.io/img/favicon/w.png';
}
if (!config.bgImage) {
    config.bgImage = '';
}
let currentUrl = window.location.href;
</script>
<template>
    <Head>
        <title>{{ props.config.metaTitle }}</title>
        <meta
            type="description"
            :content="props.config.metaDescription"
            head-key="meta-description"
        />
        <meta property="og:image" :content="props.config.ogImage" />

        <meta property="og:title" :content="props.config.metaTitle" />
        <meta property="og:type" content="website" />
        <meta property="og:email" content="info@wodo.io" />
        <meta property="og:phone_number" content="+905055719998" />
        <meta property="og:site_name" content="Wodo Network" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@wodoio" />
        <meta name="twitter:title" :content="props.config.metaTitle" />
        <meta
            name="twitter:description"
            :content="props.config.metaDescription"
        />
        <meta name="twitter:image" :content="props.config.ogImage" />
        <meta name="twitter:domain" :content="currentUrl" />
    </Head>
    <div
        id="wrapper"
        class="flex min-h-screen w-full flex-col justify-between bg-wgn-black-1"
    >
        <div class="w-full">
            <div class="flex h-1 w-full justify-between">
                <div class="w-full bg-[#FFA722]"></div>
                <div class="w-full bg-[#26E1ED]"></div>
                <div class="w-full bg-[#6100D4]"></div>
                <div class="w-full bg-[#FF3815]"></div>
            </div>
            <Disclosure as="nav" v-slot="{ open }" class="relative z-20">
                <div class="container mx-auto">
                    <div class="flex h-20 justify-between">
                        <div class="flex flex-shrink-0 items-center">
                            <Link :href="route('home')">
                                <Logo class="ml-6 w-32 sm:ml-0" />
                            </Link>
                        </div>
                        <PopoverGroup as="nav" class="hidden space-x-2 lg:flex">
                            <NavigationItem
                                :href="link.href"
                                as="link"
                                v-for="link in navigation"
                                :item="link"
                                :key="link.name"
                                :current="
                                    config.current &&
                                    config.current === link.slug
                                        ? true
                                        : link.current
                                "
                            >
                                <span
                                    class="group inline-flex items-center rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 md:px-1 xl:px-4"
                                >
                                    {{ link.name }}
                                    <ChevronDownIcon
                                        class="ml-1 h-4 w-4 text-white"
                                        aria-hidden="true"
                                        v-if="link.withIcon"
                                    />
                                </span>
                            </NavigationItem>
                        </PopoverGroup>
                        <div class="flex items-center">
                            <div class="mr-3 hidden lg:block xl:mr-16">
                                <div class="">
                                    <a
                                        href="https://gamehub.wodo.io/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <div
                                            class="game-hub-cta flex items-center justify-center rounded-[40px] border py-1 px-4 text-sm font-medium xl:text-lg"
                                        >
                                            Go to Gamehub
                                        </div>
                                    </a>
                                </div>
                            </div>

                            <!--                            <LanguageSwitch />-->
                            <div
                                class="hidden flex-shrink-0 items-center space-x-4 lg:flex"
                            >
                                <a
                                    href="https://twitter.com/wodoio"
                                    target="_blank"
                                >
                                    <TwitterIcon
                                        class="text-wgn-gray-1 opacity-70"
                                    ></TwitterIcon>
                                </a>
                                <a
                                    href="https://t.me/wodocommunity"
                                    target="_blank"
                                >
                                    <TelegramIcon
                                        class="text-wgl-gray-1 opacity-70"
                                    ></TelegramIcon>
                                </a>
                                <a
                                    href="https://discord.gg/fbyns8Egpb"
                                    target="_blank"
                                >
                                    <DiscordIcon
                                        class="text-wgn-gray-1 opacity-70"
                                    ></DiscordIcon>
                                </a>
                                <a
                                    href="https://www.youtube.com/c/Wodoio"
                                    target="_blank"
                                >
                                    <YoutubeIcon
                                        class="text-wgn-gray-1 opacity-70"
                                    ></YoutubeIcon>
                                </a>
                                <a
                                    href="https://www.linkedin.com/company/wodonetwork/"
                                    target="_blank"
                                >
                                    <LinkedinIcon
                                        class="text-wgl-gray-1 opacity-70"
                                    ></LinkedinIcon>
                                </a>
                                <!--                                <a href="https://www.instagram.com/wodoio" target="_blank">-->
                                <!--                                    <InstagramIcon class="text-wgn-gray-1 opacity-70"></InstagramIcon>-->
                                <!--                                </a>-->
                                <!--                                <a href="https://discord.gg/fbyns8Egpb" target="_blank">-->
                                <!--                                    <FacebookIcon class="text-wgn-gray-1 opacity-70"></FacebookIcon>-->
                                <!--                                </a>-->
                            </div>
                            <div class="flex-shrink-0 md:mr-4"></div>
                            <div class="ml-2 mr-2 flex items-center lg:hidden">
                                <!-- Mobile menu button -->
                                <DisclosureButton
                                    class="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                                >
                                    <span class="sr-only">Open main menu</span>
                                    <Bars3Icon
                                        v-if="!open"
                                        class="block h-6 w-6"
                                        aria-hidden="true"
                                    />
                                    <XMarkIcon
                                        v-else
                                        class="block h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </DisclosureButton>
                            </div>
                        </div>
                    </div>
                </div>

                <DisclosurePanel
                    class="absolute z-20 w-full rounded-b-2xl bg-wgn-black-2 lg:hidden"
                    v-slot="{ close }"
                >
                    <div class="space-y-2 pt-4 pb-4">
                        <div class="ml-5">
                            <div class="">
                                <a
                                    href="https://gamehub.wodo.io/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <div
                                        class="game-hub-cta flex w-fit items-center justify-center rounded-[40px] border py-1 px-4 text-lg font-medium"
                                    >
                                        Go to Gamehub
                                    </div>
                                </a>
                            </div>
                        </div>

                        <MobileNavigationItem
                            :href="link.href"
                            as="link"
                            v-for="link in navigation"
                            :item="link"
                            :key="link.name"
                            :current="link.current"
                            class="cursor-pointer"
                            @click="close"
                        >
                            <span>{{ link.name }}</span>
                        </MobileNavigationItem>
                    </div>
                    <div
                        class="mx-4 border-t border-white border-opacity-10 pt-6 pb-6 sm:mx-6"
                    >
                        <div class="flex items-center">
                            <div
                                class="flex flex-shrink-0 items-center space-x-8"
                            >
                                <a
                                    href="https://twitter.com/wodoio"
                                    target="_blank"
                                >
                                    <TwitterIcon
                                        class="text-wgn-gray-1 opacity-70"
                                    ></TwitterIcon>
                                </a>
                                <a
                                    href="https://t.me/wodocommunity"
                                    target="_blank"
                                >
                                    <TelegramIcon
                                        class="text-wgl-gray-1 opacity-70"
                                    ></TelegramIcon>
                                </a>
                                <a
                                    href="https://discord.gg/fbyns8Egpb"
                                    target="_blank"
                                >
                                    <DiscordIcon
                                        class="text-wgn-gray-1 opacity-70"
                                    ></DiscordIcon>
                                </a>
                                <a
                                    href="https://www.youtube.com/c/Wodoio"
                                    target="_blank"
                                >
                                    <YoutubeIcon
                                        class="text-wgn-gray-1 opacity-70"
                                    ></YoutubeIcon>
                                </a>
                                <a
                                    href="https://www.linkedin.com/company/wodonetwork/"
                                    target="_blank"
                                >
                                    <LinkedinIcon
                                        class="text-wgl-gray-1 opacity-70"
                                    ></LinkedinIcon>
                                </a>
                                <!--                                <a href="https://www.instagram.com/wodoio" target="_blank">-->
                                <!--                                    <InstagramIcon class="text-wgn-gray-1 opacity-70"></InstagramIcon>-->
                                <!--                                </a>-->
                                <!--                                <a href="https://discord.gg/fbyns8Egpb" target="_blank">-->
                                <!--                                    <FacebookIcon class="text-wgn-gray-1 opacity-70"></FacebookIcon>-->
                                <!--                                </a>-->
                            </div>
                        </div>
                    </div>
                </DisclosurePanel>
            </Disclosure>
        </div>

        <div
            v-if="config.current === 'home'"
            class="absolute mt-[90px] h-[349px] w-full bg-cover bg-right lg:h-screen lg:bg-center"
            style=""
            :style="'background-image: url(' + props.config.bgImage + ');'"
        ></div>
        <div
            v-if="config.current === 'home'"
            class="absolute mt-[90px] h-[349px] w-full lg:h-screen"
            style="
                background: linear-gradient(
                    180deg,
                    rgba(24, 18, 42, 0) 0%,
                    #18122a 92.71%
                );
            "
        ></div>

        <div
            v-if="config.current !== 'home' && config.current !== 'finance'"
            class="absolute mt-[90px] w-full"
            style="height: 391px; background-size: cover"
            :style="'background-image: url(' + props.config.bgImage + ');'"
        ></div>
        <div
            v-if="config.current !== 'home' && config.current !== 'finance'"
            class="absolute mt-[90px] w-full"
            style="
                height: 391px;
                background: linear-gradient(
                    180deg,
                    rgba(24, 18, 42, 0) 0%,
                    #18122a 100%
                );
            "
        ></div>
        <div
            v-if="config.current !== 'home' && config.current !== 'finance'"
            class="absolute mt-[90px] w-full"
            style="
                height: 391px;
                background: linear-gradient(
                    180deg,
                    rgba(97, 0, 212, 0.42) 0%,
                    rgba(97, 0, 212, 0) 100%
                );
            "
        ></div>

        <div
            v-if="config.current === 'finance'"
            class="absolute mt-[84px] w-full"
            style=""
        ></div>

        <div
            :class="
                config.current === 'finance'
                    ? ''
                    : 'container mx-auto mt-4 flex h-full min-h-screen flex-1 flex-grow flex-col px-4 md:mt-8'
            "
        >
            <slot />
        </div>
        <div
            :class="
                config.current === 'finance'
                    ? 'bg-wgn-purple-6'
                    : 'footer-background'
            "
        >
            <div class="container mx-auto mt-8 w-full py-2">
                <Footer :errors="errors" />
            </div>
        </div>
    </div>
</template>
<style scoped>
.game-hub-cta {
    background: linear-gradient(90deg, #360175 0%, #330d5f 53%, #32006e 100%);
    border: 1.5px solid rgba(126, 74, 203, 1);
    color: #f6f4fe;
    transition: all 0.3s;
}
.game-hub-cta:hover {
    background: linear-gradient(90deg, #6100d4 0%, #511d8e 53%, #32006e 100%);
    border: 1.5px solid rgba(181, 166, 242, 1);
}
</style>
